import pluralize from 'pluralize';
import { useCallback, useEffect, useRef, useState } from 'react';

import { useTrackShownInactivityModal } from '@air/analytics';
import { Button } from '@air/primitive-button';
import { Modal, ModalTitle } from '@air/primitive-modal';

import { PROMPT_BEFORE_IDLE_SECONDS } from '~/components/Layouts/HomeLayout/hooks/useHandleInactivity';
import { useInactivity } from '~/providers/InactivityProvider';
import { useLogout } from '~/swr-hooks/auth/useLogout';

const MS_PER_SECOND = 1000;
const REMAINING_TIME_POLL_MS = 500;

export type InactivityAlertModalProps = AirModalProps;

export const InactivityAlertModal = ({ onClose }: InactivityAlertModalProps) => {
  const dismissButtonRef = useRef<HTMLButtonElement>(null);
  const { getRemainingTime, activate } = useInactivity();
  const calculateTotalRemainingSeconds = useCallback(
    () => Math.ceil(getRemainingTime() / MS_PER_SECOND),
    [getRemainingTime],
  );
  const [totalRemainingSeconds, setTotalRemainingSeconds] = useState(calculateTotalRemainingSeconds());
  const { logout } = useLogout();
  const { trackShownInactivityModal } = useTrackShownInactivityModal();

  useEffect(() => {
    trackShownInactivityModal();
  }, [trackShownInactivityModal]);

  useEffect(() => {
    const interval = setInterval(() => {
      setTotalRemainingSeconds(calculateTotalRemainingSeconds());
    }, REMAINING_TIME_POLL_MS);

    return () => {
      clearInterval(interval);
    };
  }, [calculateTotalRemainingSeconds]);

  /**
   * There's a bug we've noticed where sometimes this modal is fired way before it should be.
   * If that happens, this code should detect that and then hide it.
   */
  useEffect(() => {
    if (totalRemainingSeconds > PROMPT_BEFORE_IDLE_SECONDS) {
      onClose();
    }
  }, [onClose, totalRemainingSeconds]);

  const minutes = Math.floor(totalRemainingSeconds / 60);
  const seconds = totalRemainingSeconds % 60;

  const onLogoutClick = useCallback(() => {
    logout();
    onClose();
  }, [logout, onClose]);

  const onStayLoggedInClick = useCallback(() => {
    activate();
    onClose();
  }, [activate, onClose]);

  return (
    <Modal isOpen>
      <header className="mb-6 flex justify-between gap-4">
        <ModalTitle>Your session is about to expire</ModalTitle>
      </header>
      <div className="ads:text-secondary text-16 text-gray-11">
        <p className="mb-4 text-16">For your security, your session will expire shortly</p>
        <p>
          You will be logged out in{' '}
          <span className="inline font-bold">
            {minutes ? pluralize('minute', minutes, true) : ''}
            {seconds && minutes ? ' ' : ''}
            {seconds ? pluralize('second', seconds, true) : ''}
          </span>
          .
        </p>
      </div>
      <div className="mt-8 flex justify-end">
        <Button onClick={onLogoutClick} appearance="ghost" color="gray" className="mr-4" size="large">
          Logout
        </Button>
        <Button ref={dismissButtonRef} onClick={onStayLoggedInClick} appearance="filled" color="blue" size="large">
          Stay logged in
        </Button>
      </div>
    </Modal>
  );
};
